import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import { useSocketContext } from '../../hooks/useSocket';
import { axiosInstance } from "../../utils/axiosInstance";
import StatusHistoryModal from "./StatusHistoryModal";
import OutOfOrderModal, { OutOfOrderModalTarget } from "./OutOfOrderModal";
import MachineContextMenu, { MachineContextMenuTarget } from "./MachineContextMenu";
import MachineStartModal, { MachineStartModalTarget } from "./MachineStartModal";
import MachineChangeCycleModal, {
  MachineChangeCycleModalTarget,
} from "./MachineChangeCycleModal";
import StatusTimeline, {
  StartAllMachine,
  StatusTimelineHandle,
} from "./StatusTimeline";
import StatusLocationCompare from "./StatusLocationCompare";
import PageContainer from "../layouts/PageContainer";
import {
  DRYER_MINUTE_OPTIONS,
  GetPriceResult,
  MachineKind,
  axiosErrorMessage,
  filterCycleCatalog,
  machineKindFromName,
} from "./machineControlTypes";
import { Location } from "../../interfaces/interfaces";
import { UserContext } from "../../context/UserContext";

const ADMIN_ROLE_ID = 1;
const START_ALL_DRYER_MINUTES = DRYER_MINUTE_OPTIONS[0];

type StartAllFailure = {
  name: string;
  macId: string;
  error: string;
};

type StartAllSummary = {
  succeeded: number;
  failed: StartAllFailure[];
  skipped: number;
};

/** Order by SQGS number (SQGS01…SQGS99) when pins match that pattern; otherwise same as settings locations list. */
function compareLocationsByPin(a: Location, b: Location): number {
  const pinA = a.locationPin || "";
  const pinB = b.locationPin || "";

  const sqgsA = /^SQGS(\d+)$/i.exec(pinA);
  const sqgsB = /^SQGS(\d+)$/i.exec(pinB);
  if (sqgsA && sqgsB) {
    return parseInt(sqgsA[1], 10) - parseInt(sqgsB[1], 10);
  }

  const numA = Number(pinA);
  const numB = Number(pinB);
  if (!isNaN(numA) && !isNaN(numB) && pinA !== "" && pinB !== "") {
    return numA - numB;
  }
  return pinA.localeCompare(pinB);
}

function resolveRequestedBy(userName?: string): string {
  if (userName) return userName;
  try {
    const raw = localStorage.getItem("userDetails");
    if (!raw) return "portal-user";
    const parsed = JSON.parse(raw);
    return parsed.userName || parsed.username || "portal-user";
  } catch {
    return "portal-user";
  }
}

type ActionBusy = {
  macId: string;
  action: "ooo" | "start" | "stop" | "set-cycle";
};

const MachinesPage: React.FC = () => {
  const { user } = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const siteIdFromUrl = searchParams.get("siteId") || "";
  const [selectedLocation, setSelectedLocation] = useState<string>(siteIdFromUrl);
  const [locationOptions, setLocationOptions] = useState<Location[]>([]);
  const [locationsLoading, setLocationsLoading] = useState<boolean>(true);
  const [locationsError, setLocationsError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<ActionBusy | null>(null);
  const [oooError, setOooError] = useState<string | null>(null);
  const [oooTarget, setOooTarget] = useState<OutOfOrderModalTarget | null>(null);
  const [contextMenu, setContextMenu] = useState<MachineContextMenuTarget | null>(null);
  const [errorHistoryTarget, setErrorHistoryTarget] = useState<{ macId: string; name: string } | null>(null);
  const [startTarget, setStartTarget] = useState<MachineStartModalTarget | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [changeCycleTarget, setChangeCycleTarget] =
    useState<MachineChangeCycleModalTarget | null>(null);
  const [changeCycleError, setChangeCycleError] = useState<string | null>(null);
  const [stopTarget, setStopTarget] = useState<{ macId: string; name: string } | null>(null);
  const [stopError, setStopError] = useState<string | null>(null);
  const [startAllPending, setStartAllPending] = useState<StartAllMachine[] | null>(null);
  const [startAllBusy, setStartAllBusy] = useState(false);
  const [startAllProgress, setStartAllProgress] = useState<{
    current: number;
    total: number;
    name: string;
  } | null>(null);
  const [startAllSummary, setStartAllSummary] = useState<StartAllSummary | null>(null);
  const timelineRef = useRef<StatusTimelineHandle>(null);

  const { isConnected, connect, emit, on } = useSocketContext();
  const requestedBy = resolveRequestedBy(user?.userName);
  const showStartAll = user?.roleId === ADMIN_ROLE_ID;

  useEffect(() => {
    setLocationsLoading(true);
    setLocationsError(null);
    axiosInstance
      .get("/location")
      .then((response) => {
        // Filter out locations without layout_site_id and sort by name
        const validLocations = response.data
          .filter((location: Location) => location.layout_site_id != null)
          .sort(compareLocationsByPin);

        setLocationOptions(validLocations);

        const match = siteIdFromUrl
          ? validLocations.find(
              (loc: Location) =>
                loc.layout_site_id != null &&
                String(loc.layout_site_id) === siteIdFromUrl
            )
          : null;

        if (match?.layout_site_id != null) {
          setSelectedLocation(String(match.layout_site_id));
        } else if (validLocations.length > 0 && !selectedLocation) {
          const firstLocationSiteId = validLocations[0].layout_site_id;
          setSelectedLocation(firstLocationSiteId ? String(firstLocationSiteId) : "");
        }

        setLocationsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching locations:", error);
        setLocationsError("Failed to load locations. Please refresh the page.");
        setLocationsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!siteIdFromUrl) return;
    if (siteIdFromUrl !== selectedLocation) {
      setSelectedLocation(siteIdFromUrl);
    }
  }, [siteIdFromUrl]);

  const handleLocationChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value;
    setSelectedLocation(next);
    if (next) {
      setSearchParams({ siteId: next }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [setSearchParams]);

  const closeOooModal = useCallback(() => {
    if (actionBusy?.action === "ooo") return;
    setOooTarget(null);
    setOooError(null);
  }, [actionBusy]);

  const handleOutOfOrderSubmit = useCallback(
    async (payload: { reason?: string }) => {
      if (!oooTarget || actionBusy) return;

      const { macId, enabled } = oooTarget;
      if (!macId) return;

      setOooError(null);
      setActionBusy({ macId, action: "ooo" });
      try {
        await axiosInstance.post(`/machine/${encodeURIComponent(macId)}/out-of-order`, {
          enabled,
          reason: payload.reason,
          requested_by: requestedBy,
        });
        timelineRef.current?.refresh();
        setOooTarget(null);
        setOooError(null);
      } catch (err: unknown) {
        setOooError(axiosErrorMessage(err, "Failed to update out-of-order"));
      } finally {
        setActionBusy(null);
      }
    },
    [oooTarget, actionBusy, requestedBy]
  );

  const openContextMenu = useCallback(
    (
      target: { macId: string; name: string; isOutOfOrder: boolean; statusLabel: string },
      x: number,
      y: number
    ) => {
      setContextMenu({
        ...target,
        mouseX: x,
        mouseY: y,
        kind: machineKindFromName(target.name),
      });
    },
    []
  );

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const handleToggleOutOfOrder = useCallback(
    (target: MachineContextMenuTarget) => {
      closeContextMenu();
      setOooError(null);
      setOooTarget({
        macId: target.macId,
        name: target.name,
        enabled: !target.isOutOfOrder,
      });
    },
    [closeContextMenu]
  );

  const handleViewErrorHistory = useCallback(
    (target: MachineContextMenuTarget) => {
      closeContextMenu();
      setErrorHistoryTarget({ macId: target.macId, name: target.name });
    },
    [closeContextMenu]
  );

  const handleOpenStart = useCallback(
    (target: MachineContextMenuTarget) => {
      closeContextMenu();
      setStartError(null);
      setStartTarget({
        macId: target.macId,
        name: target.name,
        kind: target.kind,
      });
    },
    [closeContextMenu]
  );

  const handleOpenChangeCycle = useCallback(
    (target: MachineContextMenuTarget) => {
      closeContextMenu();
      setChangeCycleError(null);
      setChangeCycleTarget({
        macId: target.macId,
        name: target.name,
        kind: target.kind,
      });
    },
    [closeContextMenu]
  );

  const handleOpenStop = useCallback(
    (target: MachineContextMenuTarget) => {
      closeContextMenu();
      setStopError(null);
      setStopTarget({ macId: target.macId, name: target.name });
    },
    [closeContextMenu]
  );

  const closeStartModal = useCallback(() => {
    if (actionBusy?.action === "start") return;
    setStartTarget(null);
    setStartError(null);
  }, [actionBusy]);

  const closeChangeCycleModal = useCallback(() => {
    if (actionBusy?.action === "set-cycle") return;
    setChangeCycleTarget(null);
    setChangeCycleError(null);
  }, [actionBusy]);

  const closeStopDialog = useCallback(() => {
    if (actionBusy?.action === "stop") return;
    setStopTarget(null);
    setStopError(null);
  }, [actionBusy]);

  const handleStartSubmit = useCallback(
    async (payload: {
      kind: MachineKind;
      cycleId?: string;
      temperatureId?: string | null;
      useCurrentCycle?: boolean;
      minutes?: number;
    }) => {
      if (!startTarget || actionBusy) return;
      const { macId } = startTarget;
      setStartError(null);
      setActionBusy({ macId, action: "start" });
      try {
        if (payload.kind === "dryer") {
          await axiosInstance.post(
            `/machine/${encodeURIComponent(macId)}/start-dryer`,
            {
              minutes: payload.minutes,
              cycleId: payload.cycleId,
              requested_by: requestedBy,
            }
          );
        } else {
          await axiosInstance.post(
            `/machine/${encodeURIComponent(macId)}/washer-start`,
            {
              cycleId: payload.cycleId,
              temperatureId: payload.temperatureId,
              useCurrentCycle: payload.useCurrentCycle,
              requested_by: requestedBy,
            }
          );
        }
        timelineRef.current?.refresh();
        setStartTarget(null);
        setStartError(null);
      } catch (err: unknown) {
        setStartError(axiosErrorMessage(err, "Failed to start machine"));
      } finally {
        setActionBusy(null);
      }
    },
    [startTarget, actionBusy, requestedBy]
  );

  const handleChangeCycleSubmit = useCallback(
    async (payload: { cycleId: string; temperatureId?: string | null }) => {
      if (!changeCycleTarget || actionBusy) return;
      const { macId } = changeCycleTarget;
      setChangeCycleError(null);
      setActionBusy({ macId, action: "set-cycle" });
      try {
        await axiosInstance.post(
          `/machine/${encodeURIComponent(macId)}/set-cycle`,
          {
            cycleId: payload.cycleId,
            temperatureId: payload.temperatureId,
            requested_by: requestedBy,
          }
        );
        timelineRef.current?.refresh();
        setChangeCycleTarget(null);
        setChangeCycleError(null);
      } catch (err: unknown) {
        setChangeCycleError(axiosErrorMessage(err, "Failed to change cycle"));
      } finally {
        setActionBusy(null);
      }
    },
    [changeCycleTarget, actionBusy, requestedBy]
  );

  const handleStopConfirm = useCallback(async () => {
    if (!stopTarget || actionBusy) return;
    const { macId } = stopTarget;
    setStopError(null);
    setActionBusy({ macId, action: "stop" });
    try {
      await axiosInstance.post(`/machine/${encodeURIComponent(macId)}/stop`, {
        requested_by: requestedBy,
      });
      timelineRef.current?.refresh();
      setStopTarget(null);
      setStopError(null);
    } catch (err: unknown) {
      setStopError(axiosErrorMessage(err, "Failed to stop machine"));
    } finally {
      setActionBusy(null);
    }
  }, [stopTarget, actionBusy, requestedBy]);

  const handleStartAllRequest = useCallback((machines: StartAllMachine[]) => {
    if (startAllBusy) return;
    setStartAllSummary(null);
    setStartAllProgress(null);
    setStartAllPending(machines);
  }, [startAllBusy]);

  const closeStartAllDialog = useCallback(() => {
    if (startAllBusy) return;
    setStartAllPending(null);
    setStartAllProgress(null);
    setStartAllSummary(null);
  }, [startAllBusy]);

  const startOneMachine = useCallback(
    async (machine: StartAllMachine): Promise<"ok" | "skipped"> => {
      const kind = machineKindFromName(machine.name);
      if (kind === "unknown") return "skipped";

      if (kind === "washer") {
        await axiosInstance.post(
          `/machine/${encodeURIComponent(machine.macId)}/washer-start`,
          {
            useCurrentCycle: true,
            requested_by: requestedBy,
          }
        );
        return "ok";
      }

      const priceRes = await axiosInstance.post(
        `/machine/${encodeURIComponent(machine.macId)}/get-price`,
        { requested_by: requestedBy }
      );
      const data = priceRes.data as GetPriceResult;
      const catalog = filterCycleCatalog(data.cycles?.cycles);
      const cycleId =
        data.pricing?.selectedCycle?.id ||
        catalog[0]?.id ||
        "";
      if (!cycleId) {
        throw new Error("No cycle available on dryer");
      }
      await axiosInstance.post(
        `/machine/${encodeURIComponent(machine.macId)}/start-dryer`,
        {
          minutes: START_ALL_DRYER_MINUTES,
          cycleId,
          requested_by: requestedBy,
        }
      );
      return "ok";
    },
    [requestedBy]
  );

  const handleStartAllConfirm = useCallback(async () => {
    if (!startAllPending || startAllBusy) return;
    const targets = startAllPending;
    setStartAllBusy(true);
    setStartAllSummary(null);

    let succeeded = 0;
    let skipped = 0;
    const failed: StartAllFailure[] = [];

    for (let i = 0; i < targets.length; i++) {
      const machine = targets[i];
      setStartAllProgress({
        current: i + 1,
        total: targets.length,
        name: machine.name || machine.macId,
      });
      try {
        const result = await startOneMachine(machine);
        if (result === "skipped") skipped += 1;
        else succeeded += 1;
      } catch (err: unknown) {
        failed.push({
          name: machine.name || machine.macId,
          macId: machine.macId,
          error: axiosErrorMessage(err, "Failed to start machine"),
        });
      }
    }

    setStartAllProgress(null);
    setStartAllBusy(false);
    setStartAllPending(null);
    setStartAllSummary({ succeeded, failed, skipped });
    timelineRef.current?.refresh();
  }, [startAllPending, startAllBusy, startOneMachine]);

  const startAllProgressLabel = startAllProgress
    ? `Starting ${startAllProgress.current}/${startAllProgress.total}…`
    : null;

  const menuBusy =
    !!contextMenu &&
    !!actionBusy &&
    actionBusy.macId === contextMenu.macId;

  return (
    <PageContainer>
      <StatusTimeline
        ref={timelineRef}
        siteId={selectedLocation}
        locationOptions={locationOptions}
        locationsLoading={locationsLoading}
        locationsError={locationsError}
        onLocationChange={handleLocationChange}
        isConnected={isConnected}
        onRetryConnect={connect}
        onRowContextMenu={openContextMenu}
        showStartAll={showStartAll}
        startAllBusy={startAllBusy}
        startAllProgressLabel={startAllProgressLabel}
        onStartAllRequest={handleStartAllRequest}
      />
      {selectedLocation && (
        <StatusLocationCompare
          locations={locationOptions}
          currentSiteId={selectedLocation}
        />
      )}

      <StatusHistoryModal
        open={!!errorHistoryTarget}
        onClose={() => setErrorHistoryTarget(null)}
        location={selectedLocation}
        macId={errorHistoryTarget?.macId || ''}
        machineName={errorHistoryTarget?.name || ''}
        on={on}
        emit={emit}
        isConnected={isConnected}
      />

      <OutOfOrderModal
        open={!!oooTarget}
        target={oooTarget}
        location={selectedLocation}
        busy={!!oooTarget && actionBusy?.action === "ooo" && actionBusy.macId === oooTarget.macId}
        error={oooError}
        onClose={closeOooModal}
        onSubmit={handleOutOfOrderSubmit}
        on={on}
        emit={emit}
        isConnected={isConnected}
      />

      <MachineStartModal
        open={!!startTarget}
        target={startTarget}
        busy={
          !!startTarget &&
          actionBusy?.action === "start" &&
          actionBusy.macId === startTarget.macId
        }
        error={startError}
        requestedBy={requestedBy}
        onClose={closeStartModal}
        onSubmit={handleStartSubmit}
      />

      <MachineChangeCycleModal
        open={!!changeCycleTarget}
        target={changeCycleTarget}
        busy={
          !!changeCycleTarget &&
          actionBusy?.action === "set-cycle" &&
          actionBusy.macId === changeCycleTarget.macId
        }
        error={changeCycleError}
        requestedBy={requestedBy}
        onClose={closeChangeCycleModal}
        onSubmit={handleChangeCycleSubmit}
      />

      <Dialog
        open={!!stopTarget}
        onClose={closeStopDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Stop {stopTarget?.name || "machine"}?</DialogTitle>
        <DialogContent>
          <p style={{ margin: "0.25rem 0 0" }}>
            Send a remote stop command to this machine.
          </p>
          {stopError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {stopError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeStopDialog}
            disabled={actionBusy?.action === "stop"}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleStopConfirm}
            disabled={actionBusy?.action === "stop"}
          >
            {actionBusy?.action === "stop" ? "Stopping…" : "Stop"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!startAllPending || startAllBusy || !!startAllSummary}
        onClose={closeStartAllDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {startAllSummary
            ? "Start all complete"
            : startAllBusy
              ? "Starting all machines…"
              : "Start all machines?"}
        </DialogTitle>
        <DialogContent>
          {startAllBusy && startAllProgress && (
            <p style={{ margin: "0.25rem 0 0" }}>
              Starting {startAllProgress.current}/{startAllProgress.total}:{" "}
              <strong>{startAllProgress.name}</strong>
            </p>
          )}
          {!startAllBusy && !startAllSummary && startAllPending && (
            <>
              <p style={{ margin: "0.25rem 0 0" }}>
                Start{" "}
                <strong>{startAllPending.length}</strong> machine
                {startAllPending.length === 1 ? "" : "s"} at this site?
              </p>
              <p style={{ margin: "0.75rem 0 0", color: "#495057", fontSize: "0.9rem" }}>
                Washers will start their current cycle. Dryers will run{" "}
                {START_ALL_DRYER_MINUTES} minutes on their current heat setting.
                Machines start one at a time.
              </p>
            </>
          )}
          {startAllSummary && (
            <>
              <p style={{ margin: "0.25rem 0 0" }}>
                Succeeded: <strong>{startAllSummary.succeeded}</strong>
                {startAllSummary.skipped > 0 && (
                  <>
                    {" "}
                    · Skipped: <strong>{startAllSummary.skipped}</strong>
                  </>
                )}
                {startAllSummary.failed.length > 0 && (
                  <>
                    {" "}
                    · Failed: <strong>{startAllSummary.failed.length}</strong>
                  </>
                )}
              </p>
              {startAllSummary.failed.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
                    {startAllSummary.failed.map((f) => (
                      <li key={f.macId}>
                        {f.name}: {f.error}
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {startAllSummary ? (
            <Button onClick={closeStartAllDialog} variant="contained">
              Done
            </Button>
          ) : (
            <>
              <Button onClick={closeStartAllDialog} disabled={startAllBusy}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={handleStartAllConfirm}
                disabled={startAllBusy || !startAllPending?.length}
              >
                {startAllBusy ? "Starting…" : "Start all"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <MachineContextMenu
        target={contextMenu}
        busy={menuBusy}
        onClose={closeContextMenu}
        onStart={handleOpenStart}
        onStop={handleOpenStop}
        onChangeCycle={handleOpenChangeCycle}
        onToggleOutOfOrder={handleToggleOutOfOrder}
        onViewErrorHistory={handleViewErrorHistory}
      />
    </PageContainer>
  );
};

export default MachinesPage;
